<script setup lang="ts">
import { ChatAgentSidePanel } from '@dashhudson/dashing-ui/chat-agent-side-panel';
import { ChatBubble, ChatBubbleHeader } from '@dashhudson/dashing-ui/chat-bubble';
import { useDrawer } from '@dashhudson/dashing-ui/drawer';
import { useSidePanel } from '@dashhudson/dashing-ui/side-panel';
import { computed, onBeforeMount, onBeforeUnmount, ref, watch } from 'vue';

import { useProgressiveLoading } from '@/components/VisionAi/chat-panel-components/composables/useProgressiveLoading';
import { useSubmitUserPrompt } from '@/components/VisionAi/chat-panel-components/composables/useSubmitUserPrompt';
import { ChatMessageRole } from '@/components/VisionAi/chat-panel-components/utils/VisionChatSidePanel.types';
import VisionAiChatButton from '@/components/VisionAi/chat-panel-components/VisionAiChatButton.vue';
import VisionChatHistory from '@/components/VisionAi/chat-panel-components/VisionChatHistory.vue';
import VisionChatPanelHeader from '@/components/VisionAi/chat-panel-components/VisionChatPanelHeader.vue';
import { VISION_AI_PANEL_TYPE, VISION_PANEL_WIDTH_EXPANDED } from '@/components/VisionAi/constants';
import { useIdentityStore } from '@/stores/identity';
import { useVisionAiStore } from '@/stores/vision-ai';
import { useVisionAiConversationsStore } from '@/stores/vision-ai-conversations';
import { useVisionAiMessagesStore } from '@/stores/vision-ai-messages';
import type { Identity } from '@/types/auth.types';
import { formatDate, formatTimestamp } from '@/utils/vision';

const identityStore = useIdentityStore();
const visionAiStore = useVisionAiStore();
const visionAiConversationsStore = useVisionAiConversationsStore();
const visionAiMessagesStore = useVisionAiMessagesStore();

const userName = computed(() => (identityStore.identity as Identity | null)?.first_name);
const userAvatarUrl = computed(() => (identityStore.identity as Identity | null)?.avatar_url);

// Bypass the expandable/expanded system and manage width directly with setWidth.
// Using ChatAgentSidePanel's expandable prop would place the expand button after #actions
// in SidePanelHeader, but we need the three-dot menu to render left of the expand button.
const { open, close, type, visible, setWidth } = useSidePanel();
const { visible: drawerVisible } = useDrawer();
const promptValue = ref('');

const EXPANDED_STORAGE_KEY = `chat-panel-${VISION_AI_PANEL_TYPE}-expanded`;
const storedExpanded = sessionStorage.getItem(EXPANDED_STORAGE_KEY);
const isExpanded = ref(storedExpanded !== 'false');

watch(isExpanded, (val) => {
  sessionStorage.setItem(EXPANDED_STORAGE_KEY, JSON.stringify(val));
});

// While a drawer is open, force the chat panel to its collapsed width so it
// doesn't cover the drawer. The user's isExpanded preference is preserved and
// restored when the drawer closes.
function applyPanelWidth() {
  const shouldExpand = isExpanded.value && !drawerVisible.value;
  setWidth(shouldExpand ? VISION_PANEL_WIDTH_EXPANDED : null);
}

watch(drawerVisible, () => {
  if (!visible.value || type.value !== VISION_AI_PANEL_TYPE) return;
  applyPanelWidth();
});

const isLoading = computed(() => visionAiStore.loadingMessageInCurrentConversation);

const { messagesWithLoading } = useProgressiveLoading(
  computed(() => visionAiMessagesStore.messages),
  isLoading,
);

const { handleSubmit } = useSubmitUserPrompt(promptValue);

function getDateDividerText(index: number): string | null {
  const msgs = messagesWithLoading.value;
  const current = msgs[index];
  if (!current || current.isLoading) return null;
  const currentDate = formatDate(current.timestamp);
  if (index === 0) return currentDate;
  const prev = msgs[index - 1];
  if (prev?.isLoading) return null;
  const previousDate = formatDate(prev.timestamp);
  return currentDate !== previousDate ? currentDate : null;
}

const formattedMessages = computed(() =>
  messagesWithLoading.value.map((msg, index) => ({
    ...msg,
    isAssistant: msg.role === ChatMessageRole.Assistant,
    name: msg.role === ChatMessageRole.User ? userName.value || 'You' : undefined,
    avatarSrc: msg.role === ChatMessageRole.User ? userAvatarUrl.value : undefined,
    formattedTimestamp: msg.isLoading ? undefined : formatTimestamp(msg.timestamp),
    dateDividerText: getDateDividerText(index),
  })),
);

async function toggleChatWindow() {
  if (visible.value && type.value === VISION_AI_PANEL_TYPE) {
    close();
  } else {
    await open(VISION_AI_PANEL_TYPE);
    applyPanelWidth();
  }
}

onBeforeMount(() => {
  visionAiConversationsStore.getConversations({});
});

onBeforeUnmount(() => {
  if (type.value === VISION_AI_PANEL_TYPE) {
    close();
  }
});
</script>

<template>
  <VisionAiChatButton @toggle-chat-window="toggleChatWindow" />
  <ChatAgentSidePanel
    v-model:prompt-value="promptValue"
    :type="VISION_AI_PANEL_TYPE"
    :messages="formattedMessages"
    :messages-key="visionAiConversationsStore.currentConversationId"
    :loading="isLoading"
    :sidebar-min-panel-width="VISION_PANEL_WIDTH_EXPANDED"
    placeholder="Ask Vision AI anything"
    sidebar-toggleable
    sidebar-visible
    :sidebar-storage-key="'vision-chat-sidebar'"
    @submit="handleSubmit"
  >
    <template #sidebar>
      <div class="flex min-h-0 w-[240px] flex-1 flex-col">
        <VisionChatHistory />
      </div>
    </template>

    <template #header>
      <VisionChatPanelHeader v-model:is-expanded="isExpanded" />
    </template>

    <template #empty>
      <ChatBubble variant="assistant">
        <template #header>
          <ChatBubbleHeader is-assistant />
        </template>
        <p class="typo-base">
          <strong>Hi! I'm Vision AI - now smarter and ready to help with your social data!</strong>
        </p>
        <p class="typo-base mt-4">
          Ask me anything about your social performance or click Summarize on select pages.
        </p>
        <p class="typo-base mt-4">
          I can analyze trends, compare metrics, or break down post performance - just tell me what
          you need!
        </p>
      </ChatBubble>
    </template>

    <template #message="{ message }">
      <div v-if="message.dateDividerText" class="relative mb-4 mt-2">
        <div class="h-px bg-white" />
        <div
          class="absolute -top-2 left-1/2 inline-flex h-4 min-w-[120px] -translate-x-1/2 items-center justify-center rounded-[20px] bg-white px-2"
        >
          <span class="text-[10px] font-medium text-stone-500">{{ message.dateDividerText }}</span>
        </div>
      </div>
      <ChatBubble :variant="message.role">
        <template #header>
          <ChatBubbleHeader
            :is-assistant="message.isAssistant"
            :name="message.name"
            :avatar-src="message.avatarSrc"
            :timestamp="message.formattedTimestamp"
          />
        </template>

        <template v-if="message.isLoading">
          <p class="typo-base text-secondary">{{ message.text }}</p>
        </template>

        <template v-else-if="message.role === ChatMessageRole.User">
          <p class="typo-base">{{ message.text }}</p>
        </template>

        <template v-else>
          <p class="typo-base whitespace-pre-wrap">{{ message.text }}</p>
        </template>
      </ChatBubble>
    </template>
  </ChatAgentSidePanel>
</template>
